import React, { useState } from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Tabs,
    Tab,
    Container,
    useTheme,
    alpha
} from '@mui/material';
import {
    Verified as QualityControlIcon,
    LocalDrink as BottleIcon,
    FiberManualRecord as CapIcon,
    Label as LabelIcon,
    Inventory as SupplyIcon,
    Biotech as RawMaterialIcon
} from '@mui/icons-material';
import SupplyQCTests from './components/SupplyQCTests';
import RawMaterialQCTests from './components/RawMaterialQCTests';
import { usePermissions } from '../../../utils/permissions';

const QCTests = () => {
    const theme = useTheme();
    const { hasPermission } = usePermissions();
    const [activeTab, setActiveTab] = useState(0);

    // Check permissions for both QC test types
    const canViewSupplyQC = hasPermission('inventory.supplies.qc_tests.view');
    const canViewRawMaterialQC = hasPermission('inventory.raw_materials.qc_tests.view');

    // If user has no access to either, show access restricted
    if (!canViewSupplyQC && !canViewRawMaterialQC) {
        return (
            <Container maxWidth="xl" sx={{ py: 4 }}>
                <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    minHeight: '70vh',
                    textAlign: 'center'
                }}>
                    <Card sx={{
                        p: 8,
                        borderRadius: 3,
                        border: `1px solid ${theme.palette.grey[400]}`,
                        background: 'white',
                        boxShadow: `0 12px 40px ${alpha(theme.palette.grey[900], 0.08)}`,
                        maxWidth: 600,
                        width: '100%',
                        position: 'relative',
                        overflow: 'hidden',
                        cursor: 'default',
                        transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                        '&:hover': {
                            transform: 'translateY(-8px)',
                            boxShadow: `0 20px 60px ${alpha(theme.palette.grey[900], 0.15)}`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.3)}`
                        },
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            height: 4,
                            background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.grey[400]})`,
                            opacity: 0.8,
                            transition: 'opacity 0.3s ease'
                        },
                        '&:hover::before': {
                            opacity: 1
                        }
                    }}>
                        <Box sx={{ 
                            p: 3, 
                            borderRadius: 3, 
                            background: `linear-gradient(${alpha(theme.palette.primary.main, 0.05)}, ${alpha(theme.palette.primary.main, 0.02)})`,
                            border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                            mb: 6,
                            display: 'inline-flex',
                            boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.15)}`
                        }}>
                            <QualityControlIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
                        </Box>
                        <Typography variant="h3" sx={{ 
                            mb: 3, 
                            fontWeight: 500, 
                            color: 'text.primary',
                            fontSize: '2.5rem',
                            letterSpacing: '-0.8px'
                        }}>
                            Access Restricted
                        </Typography>
                        <Typography variant="h6" sx={{ 
                            color: 'text.secondary', 
                            mb: 4,
                            fontWeight: 500,
                            fontSize: '1.2rem'
                        }}>
                            Quality Control Permissions Required
                        </Typography>
                        <Typography variant="body1" sx={{ 
                            color: 'text.secondary',
                            lineHeight: 1.7,
                            mb: 6,
                            fontSize: '1.05rem',
                            maxWidth: 450,
                            mx: 'auto'
                        }}>
                            You don't currently have the necessary permissions to access Quality Control Tests. 
                            Contact your system administrator to request access or verify your role permissions.
                        </Typography>
                    </Card>
                </Box>
            </Container>
        );
    }

    // Create available tabs based on permissions
    const availableTabs = [];
    if (canViewRawMaterialQC) {
        availableTabs.push({ 
            label: "Chemical Quality Control Checks", 
            component: <RawMaterialQCTests />,
            key: 'raw_material'
        });
    }
    if (canViewSupplyQC) {
        availableTabs.push({ 
            label: "Supply Quality Control Checks", 
            component: <SupplyQCTests />,
            key: 'supply'
        });
    }

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    return (
        <Container maxWidth="xl" sx={{ py: 4 }}>
            {/* Enhanced Page Header */}
            <Card sx={{ 
                borderRadius: 3,
                mt: 5,
                mb: 3,
                border: `1px solid ${theme.palette.grey[400]}`,
                background: theme.palette.grey[100],
                backdropFilter: 'blur(20px)',
                borderTop: '3px solid #E91E63',
                boxShadow: 'none',
                position: 'relative',
                overflow: 'hidden',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                },
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            }}>
                <CardContent sx={{ p: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2.5, mb: 2 }}>
                        <Box sx={{
                            p: 2,
                            borderRadius: 2,
                            bgcolor: alpha('#E91E63', 0.08),
                        }}>
                            <QualityControlIcon sx={{ fontSize: 32, color: '#E91E63' }} />
                        </Box>
                        <Box sx={{ flex: 1 }}>
                            <Typography variant="h4" sx={{
                                fontWeight: 500,
                                color: '#E91E63',
                                mb: 1,
                                fontSize: '2rem',
                                letterSpacing: '-0.5px'
                            }}>
                                Quality Control
                            </Typography>
                            <Typography variant="subtitle1" sx={{ 
                                color: 'text.secondary', 
                                fontWeight: 500,
                                fontSize: '1rem'
                            }}>
                                Quality Control Center
                            </Typography>
                        </Box>
                    </Box>
                    <Typography variant="body2" sx={{ 
                        color: 'text.secondary', 
                        maxWidth: 700, 
                        lineHeight: 1.6,
                        fontSize: '0.95rem',
                        fontWeight: 500
                    }}>
                        Test incoming inventory items and manage quality control procedures to ensure products 
                        meet specifications before entering production workflows.
                    </Typography>
                </CardContent>
            </Card>

            {/* Tabs Container */}
            <Card sx={{ 
                borderRadius: 2, 
                border: `1px solid ${theme.palette.grey[400]}`,
                background: 'white',
                boxShadow: `0 4px 16px ${alpha(theme.palette.primary.main, 0.08)}`,
                overflow: 'hidden',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                cursor: 'default',
                '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: `0 12px 48px ${alpha(theme.palette.primary.main, 0.18)}`,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                }
            }}>
                {availableTabs.length > 1 && (
                    <Box sx={{ borderBottom: `1px solid ${alpha(theme.palette.grey[300], 0.8)}` }}>
                        <Tabs 
                            value={activeTab} 
                            onChange={handleTabChange}
                            sx={{
                                px: 1.5,
                                '& .MuiTabs-indicator': {
                                    backgroundColor: theme.palette.primary.main,
                                    height: 3,
                                    borderRadius: '3px 3px 0 0'
                                },
                                '& .MuiTab-root': {
                                    textTransform: 'none',
                                    fontWeight: 500,
                                    fontSize: '0.9rem',
                                    minHeight: 48,
                                    px: 2,
                                    color: theme.palette.text.secondary,
                                    '&.Mui-selected': {
                                        color: theme.palette.primary.main,
                                        fontWeight: 500
                                    },
                                    '&:hover': {
                                        color: theme.palette.primary.main,
                                        backgroundColor: alpha(theme.palette.primary.main, 0.04)
                                    }
                                }
                            }}
                        >
                            {availableTabs.map((tab, index) => (
                                <Tab 
                                    key={tab.key}
                                    label={tab.label}
                                />
                            ))}
                        </Tabs>
                    </Box>
                )}

                <Box>
                    {availableTabs.map((tab, index) => (
                        activeTab === index && tab.component
                    ))}
                </Box>
            </Card>
        </Container>
    );
};

export default QCTests;